import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Cookie, X, Settings2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";

const CookieConsent = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    aiProcessing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      // Small delay before showing banner
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    const consentData = {
      necessary: true,
      analytics: true,
      marketing: true,
      aiProcessing: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("cookieConsent", JSON.stringify(consentData));
    setIsVisible(false);
  };

  const acceptNecessary = () => {
    const consentData = {
      necessary: true,
      analytics: false,
      marketing: false,
      aiProcessing: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("cookieConsent", JSON.stringify(consentData));
    setIsVisible(false);
  };

  const savePreferences = () => {
    const consentData = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("cookieConsent", JSON.stringify(consentData));
    setIsVisible(false);
    setShowPreferences(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
        >
          <Card className="max-w-4xl mx-auto bg-card/95 backdrop-blur-xl border-border shadow-2xl overflow-hidden">
            <div className="p-4 md:p-6">
              <div className="flex items-start gap-4">
                <div className="hidden md:flex p-3 rounded-xl bg-accent/10">
                  <Cookie className="h-6 w-6 text-accent" />
                </div>
                
                <div className="flex-1 space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-lg flex items-center gap-2">
                        <Cookie className="h-5 w-5 text-accent md:hidden" />
                        {t("cookies.title")}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                        {t("cookies.description")}{" "}
                        <Link to="/privacy" className="text-accent hover:underline">
                          {t("cookies.privacyPolicy")}
                        </Link>
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="flex-shrink-0 -mt-1 -mr-1"
                      onClick={acceptNecessary}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  {showPreferences && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="space-y-3 pt-3 border-t border-border"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{t("cookies.necessary")}</p>
                          <p className="text-xs text-muted-foreground">{t("cookies.necessaryDesc")}</p>
                        </div>
                        <div className="px-3 py-1 rounded-full bg-muted text-muted-foreground text-xs">
                          {t("cookies.alwaysOn")}
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{t("cookies.analytics")}</p>
                          <p className="text-xs text-muted-foreground">{t("cookies.analyticsDesc")}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={preferences.analytics}
                            onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-accent rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-accent"></div>
                        </label>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{t("cookies.marketing")}</p>
                          <p className="text-xs text-muted-foreground">{t("cookies.marketingDesc")}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={preferences.marketing}
                            onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-accent rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-accent"></div>
                        </label>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">{t("cookies.aiProcessing")}</p>
                          <p className="text-xs text-muted-foreground">{t("cookies.aiProcessingDesc")}</p>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={preferences.aiProcessing}
                            onChange={(e) => setPreferences({ ...preferences, aiProcessing: e.target.checked })}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-accent rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-muted after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-accent"></div>
                        </label>
                      </div>
                    </motion.div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={() => setShowPreferences(!showPreferences)}
                    >
                      <Settings2 className="h-4 w-4" />
                      {showPreferences ? t("cookies.hidePreferences") : t("cookies.managePreferences")}
                    </Button>
                    <div className="flex gap-2 flex-1 sm:justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={acceptNecessary}
                        className="flex-1 sm:flex-none"
                      >
                        {t("cookies.necessaryOnly")}
                      </Button>
                      {showPreferences ? (
                        <Button
                          size="sm"
                          onClick={savePreferences}
                          className="flex-1 sm:flex-none bg-accent hover:bg-accent/90"
                        >
                          {t("cookies.savePreferences")}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={acceptAll}
                          className="flex-1 sm:flex-none bg-accent hover:bg-accent/90"
                        >
                          {t("cookies.acceptAll")}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
